/**
 * Localization messages and helpers for PixelBlock blocks.
 * Supports:
 * - 'fr': Scratch official blocks' names in French
 * - 'en': Scratch official blocks' names in English
 * - 'python': Python blocks' names
 */

export const PIXELBLOCK_MESSAGES = {
    en: {
        // Output / Looks
        PRINT: 'say %1',
        PRINT_MULTI: 'say %1 , %2',
        PRINT_THREE: 'say %1 , %2 , %3',
        PRINT_FOUR: 'say %1 , %2 , %3 , %4',
        PRINT_FIVE: 'say %1 , %2 , %3 , %4 , %5',
        PRINT_PREFIX: 'say',
        PRINT_SUFFIX: '',
        CTX_ADD_PRINT_PART: 'Add value',
        CTX_REMOVE_PRINT_PART: 'Remove last value',

        // Control
        IF: 'if %1 then',
        ELIF_LABEL: 'else if',
        THEN_LABEL: 'then',
        ELSE_LABEL: 'else',
        CTX_ADD_ELSE: 'Add else',
        CTX_ADD_ELIF: 'Add else if',
        CTX_REMOVE_BRANCH: 'Remove last branch',
        FOR_RANGE: 'repeat with %1 from %2 to %3',
        FOR_RANGE_STEP: 'repeat with %1 from %2 to %3 by %4',
        FOR_IN: 'for each %1 in %2',
        WHILE: 'repeat while %1',
        BREAK: 'stop this loop',
        CONTINUE: 'next iteration',
        PASS: 'pass',

        // Input / Sensing
        INPUT: 'ask %1 and wait',
        INPUT_INT: 'ask %1 and wait (number)',
        INPUT_FLOAT: 'ask %1 and wait (decimal)',

        // Operators
        ROUND: 'round %1',
        ABS: 'abs of %1',
        MIN_MAX: '%1 of %2 and %3',
        NOT: 'not %1',
        NONE: 'none',
        CAST_INT: 'to integer %1',
        CAST_FLOAT: 'to decimal %1',
        CAST_STR: 'to text %1',
        CAST_BOOL: 'to boolean %1',
        TYPE: 'type of %1',

        // Variables
        VAR_SET: 'set %1 to %2',
        VAR_CHANGE: '%1 %2 by %3',

        // Lists
        LIST_EMPTY: 'empty list',
        LIST_CREATE_3: 'list with %1 , %2 , %3',
        LIST_APPEND: 'add %2 to %1',
        LIST_POP: 'delete item %2 of %1',
        LIST_REMOVE: 'delete %2 of %1',
        LIST_INSERT: 'insert %3 at %2 of %1',
        LIST_GET: 'item %2 of %1',
        LIST_SET: 'replace item %2 of %1 with %3',
        LIST_LEN: 'length of %1',
        RANGE: 'numbers 0 to %1',
        RANGE_START_STOP: 'numbers %1 to %2',

        // Functions
        DEF: 'define %1 ( )',
        RETURN: 'return %1',
        DICT_EMPTY: 'empty dict',
        COMMENT: '# %1'
    },

    fr: {
        // Sortie / Apparence
        PRINT: 'dire %1',
        PRINT_MULTI: 'dire %1 , %2',
        PRINT_THREE: 'dire %1 , %2 , %3',
        PRINT_FOUR: 'dire %1 , %2 , %3 , %4',
        PRINT_FIVE: 'dire %1 , %2 , %3 , %4 , %5',
        PRINT_PREFIX: 'dire',
        PRINT_SUFFIX: '',
        CTX_ADD_PRINT_PART: 'Ajouter une valeur',
        CTX_REMOVE_PRINT_PART: 'Supprimer la dernière valeur',

        // Contrôle
        IF: 'si %1 alors',
        ELIF_LABEL: 'sinon si',
        THEN_LABEL: 'alors',
        ELSE_LABEL: 'sinon',
        CTX_ADD_ELSE: 'Ajouter sinon',
        CTX_ADD_ELIF: 'Ajouter sinon si',
        CTX_REMOVE_BRANCH: 'Supprimer la dernière branche',
        FOR_RANGE: 'pour %1 allant de %2 à %3',
        FOR_RANGE_STEP: 'pour %1 allant de %2 à %3 par pas de %4',
        FOR_IN: 'pour chaque %1 dans %2',
        WHILE: 'répéter tant que %1',
        BREAK: 'quitter la boucle',
        CONTINUE: 'itération suivante',
        PASS: 'passer',

        // Entrée / Capteurs
        INPUT: 'demander %1 et attendre',
        INPUT_INT: 'demander %1 et attendre (entier)',
        INPUT_FLOAT: 'demander %1 et attendre (décimal)',

        // Opérateurs
        ROUND: 'arrondi de %1',
        ABS: 'abs de %1',
        MIN_MAX: '%1 de %2 et %3',
        NOT: 'non %1',
        NONE: 'rien',
        CAST_INT: 'en entier %1',
        CAST_FLOAT: 'en décimal %1',
        CAST_STR: 'en texte %1',
        CAST_BOOL: 'en booléen %1',
        TYPE: 'type de %1',

        // Variables
        VAR_SET: 'mettre %1 à %2',
        VAR_CHANGE: '%1 %2 de %3',

        // Listes
        LIST_EMPTY: 'liste vide',
        LIST_CREATE_3: 'liste avec %1 , %2 , %3',
        LIST_APPEND: 'ajouter %2 à %1',
        LIST_POP: 'supprimer élément %2 de %1',
        LIST_REMOVE: 'supprimer %2 de %1',
        LIST_INSERT: 'insérer %3 en position %2 de %1',
        LIST_GET: 'élément %2 de %1',
        LIST_SET: 'remplacer l\'élément %2 de %1 par %3',
        LIST_LEN: 'longueur de %1',
        RANGE: 'nombres de 0 à %1',
        RANGE_START_STOP: 'nombres de %1 à %2',

        // Fonctions
        DEF: 'définir %1 ( )',
        RETURN: 'renvoyer %1',
        DICT_EMPTY: 'dictionnaire vide',
        COMMENT: '# %1'
    },

    python: {
        // Output / Looks
        PRINT: 'print ( %1 )',
        PRINT_MULTI: 'print ( %1 , %2 )',
        PRINT_THREE: 'print ( %1 , %2 , %3 )',
        PRINT_FOUR: 'print ( %1 , %2 , %3 , %4 )',
        PRINT_FIVE: 'print ( %1 , %2 , %3 , %4 , %5 )',
        PRINT_PREFIX: 'print (',
        PRINT_SUFFIX: ')',
        CTX_ADD_PRINT_PART: 'Add argument',
        CTX_REMOVE_PRINT_PART: 'Remove argument',

        // Control
        IF: 'if %1 :',
        ELIF_LABEL: 'elif',
        THEN_LABEL: ':',
        ELSE_LABEL: 'else :',
        CTX_ADD_ELSE: 'Add else',
        CTX_ADD_ELIF: 'Add elif',
        CTX_REMOVE_BRANCH: 'Remove last branch',
        FOR_RANGE: 'for %1 in range ( %2 , %3 ) :',
        FOR_RANGE_STEP: 'for %1 in range ( %2 , %3 , %4 ) :',
        FOR_IN: 'for %1 in %2 :',
        WHILE: 'while %1 :',
        BREAK: 'break',
        CONTINUE: 'continue',
        PASS: 'pass',

        // Input / Sensing
        INPUT: 'input ( %1 )',
        INPUT_INT: 'int ( input ( %1 ) )',
        INPUT_FLOAT: 'float ( input ( %1 ) )',

        // Operators
        ROUND: 'round ( %1 )',
        ABS: 'abs ( %1 )',
        MIN_MAX: '%1 ( %2 , %3 )',
        NOT: 'not %1',
        NONE: 'None',
        CAST_INT: 'int ( %1 )',
        CAST_FLOAT: 'float ( %1 )',
        CAST_STR: 'str ( %1 )',
        CAST_BOOL: 'bool ( %1 )',
        TYPE: 'type ( %1 )',

        // Variables
        VAR_SET: 'set %1 = %2',
        VAR_CHANGE: '%2 %1 %3',

        // Lists
        LIST_EMPTY: '[ ]',
        LIST_CREATE_3: '[ %1 , %2 , %3 ]',
        LIST_APPEND: '%1 . append ( %2 )',
        LIST_POP: '%1 . pop ( %2 )',
        LIST_REMOVE: '%1 . remove ( %2 )',
        LIST_INSERT: '%1 . insert ( %2 , %3 )',
        LIST_GET: '%1 [ %2 ]',
        LIST_SET: '%1 [ %2 ] = %3',
        LIST_LEN: 'len ( %1 )',
        RANGE: 'range ( %1 )',
        RANGE_START_STOP: 'range ( %1 , %2 )',

        // Functions
        DEF: 'def %1 ( ) :',
        RETURN: 'return %1',
        DICT_EMPTY: '{ }',
        COMMENT: '# %1'
    }
};

export function getPixelBlockLocale() {
    if (typeof window !== 'undefined' && window.pixelblockCurrentLocale) {
        const raw = String(window.pixelblockCurrentLocale).toLowerCase();
        if (raw === 'python' || raw === 'py') return 'python';
        if (raw.startsWith('fr')) return 'fr';
        return 'en';
    }
    return 'en';
}

export function getPixelBlockMsg(key, defaultVal) {
    const loc = getPixelBlockLocale();
    if (PIXELBLOCK_MESSAGES[loc] && PIXELBLOCK_MESSAGES[loc][key]) {
        return PIXELBLOCK_MESSAGES[loc][key];
    }
    if (PIXELBLOCK_MESSAGES.en && PIXELBLOCK_MESSAGES.en[key]) {
        return PIXELBLOCK_MESSAGES.en[key];
    }
    return defaultVal;
}

export function getArithmeticOptions(loc = getPixelBlockLocale()) {
    if (loc === 'python') {
        return [
            ['+', '+'],
            ['-', '-'],
            ['*', '*'],
            ['/', '/'],
            ['//', '//'],
            ['%', '%'],
            ['**', '**']
        ];
    }
    if (loc === 'fr') {
        return [
            ['+', '+'],
            ['-', '-'],
            ['*', '*'],
            ['/', '/'],
            ['//', '//'],
            ['modulo', '%'],
            ['^', '**']
        ];
    }
    return [
        ['+', '+'],
        ['-', '-'],
        ['*', '*'],
        ['/', '/'],
        ['//', '//'],
        ['mod', '%'],
        ['^', '**']
    ];
}

export function getCompareOptions(loc = getPixelBlockLocale()) {
    if (loc === 'python') {
        return [
            ['<', '<'],
            ['<=', '<='],
            ['==', '=='],
            ['!=', '!='],
            ['>', '>'],
            ['>=', '>='],
            ['in', 'in'],
            ['not in', 'not in']
        ];
    }
    if (loc === 'fr') {
        return [
            ['<', '<'],
            ['<=', '<='],
            ['=', '=='],
            ['≠', '!='],
            ['>', '>'],
            ['>=', '>='],
            ['dans', 'in'],
            ['pas dans', 'not in']
        ];
    }
    return [
        ['<', '<'],
        ['<=', '<='],
        ['=', '=='],
        ['≠', '!='],
        ['>', '>'],
        ['>=', '>='],
        ['in', 'in'],
        ['not in', 'not in']
    ];
}

export function getBoolOpOptions(loc = getPixelBlockLocale()) {
    if (loc === 'fr') {
        return [
            ['et', 'and'],
            ['ou', 'or']
        ];
    }
    return [
        ['and', 'and'],
        ['or', 'or']
    ];
}

export function getBooleanOptions(loc = getPixelBlockLocale()) {
    if (loc === 'python') {
        return [
            ['True', 'True'],
            ['False', 'False']
        ];
    }
    if (loc === 'fr') {
        return [
            ['vrai', 'True'],
            ['faux', 'False']
        ];
    }
    return [
        ['true', 'True'],
        ['false', 'False']
    ];
}

export function getVarChangeOptions(loc = getPixelBlockLocale()) {
    if (loc === 'python') {
        return [
            ['+=', '+'],
            ['-=', '-'],
            ['*=', '*'],
            ['/=', '/']
        ];
    }
    if (loc === 'fr') {
        return [
            ['augmenter', '+'],
            ['diminuer', '-'],
            ['multiplier', '*'],
            ['diviser', '/']
        ];
    }
    return [
        ['increase', '+'],
        ['decrease', '-'],
        ['multiply', '*'],
        ['divide', '/']
    ];
}

export function registerPixelBlockLocales(ScratchBlocks) {
    if (!ScratchBlocks || !ScratchBlocks.ScratchMsgs || !ScratchBlocks.ScratchMsgs.locales) {
        return;
    }

    const { locales } = ScratchBlocks.ScratchMsgs;

    // Set Category and Variable button names for en
    if (locales.en) {
        locales.en.CATEGORY_LOOKS = 'Output';
        locales.en.CATEGORY_SENSING = 'Input';
        locales.en.CATEGORY_LISTS = 'Lists';
        locales.en.NEW_VARIABLE = 'Make a Variable';
    }

    // Set Category and Variable button names for fr
    if (locales.fr) {
        locales.fr.CATEGORY_LOOKS = 'Sortie';
        locales.fr.CATEGORY_SENSING = 'Entrée';
        locales.fr.CATEGORY_LISTS = 'Listes';
        locales.fr.NEW_VARIABLE = 'Créer une variable';
    }

    // Define 'python' locale
    if (!locales.python) {
        locales.python = Object.assign({}, locales.en, {
            CATEGORY_LOOKS: 'Output',
            CATEGORY_CONTROL: 'Control',
            CATEGORY_SENSING: 'Input',
            CATEGORY_OPERATORS: 'Operators',
            CATEGORY_VARIABLES: 'Variables',
            CATEGORY_LISTS: 'Lists',
            NEW_VARIABLE: 'Create Variable'
        });
    }
}
